'use client';

import { useEffect, useMemo, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import { Container, Section } from '@bytel/trilogy-react';

import { cmsService } from '@services/cms';
import { getLocalVariable } from '@services/storage';

import { HomePackCategory, HomePackResponseWithCategories, PACK_HASH, StaticPackProduct } from '@app-types/home-pack';
import { FaiType, OffersByCategoryCode } from '@app-types/plan';

import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { LetterVariant } from '@components/letter-variant';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { PAGE_SLUG, partials } from '@helpers/partials';
import { noop } from '@helpers/try-catch';

import { PackConfigType, filterCategoryByDate } from '@home-pack/helpers';

import { Breadcrumb } from './breadcrumb';
import { PackBody } from './pack-body';

const OVERRIDE_DATE_STORAGE_KEY = 'pack-date-override';

const getOverrideDate = () => {
    const storedValue = getLocalVariable(OVERRIDE_DATE_STORAGE_KEY);
    if (!storedValue) {
        return new Date();
    }

    const parsed = new Date();
    parsed.setTime(Number(storedValue));
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

export type PackProps = {
    defaultPlans: FaiType[];
    config: { [index: string]: PackConfigType };
    staticsCategories?: HomePackResponseWithCategories;
    currentStaticCategory?: HomePackCategory;
    faqQuestionList?: {
        question: string;
        answer: string;
        id: number;
    }[];
    trackingData: {
        page: string;
        marketLine: string;
    };
    offersByCategory: OffersByCategoryCode;
};

export function Pack({
    defaultPlans,
    config,
    staticsCategories,
    currentStaticCategory,
    trackingData,
    offersByCategory,
}: Readonly<PackProps>) {
    const [selectedStaticProduct, setSelectedStaticProduct] = useState<StaticPackProduct | null>({
        mainProduct: null,
        details: null,
    });
    const [productDetailsCms, setProductDetailsCms] = useState<string>();
    const [timelineDetailsCms, setTimelineDetailsCms] = useState<string>();
    const [currentCategory, setCurrentCategory] = useState<HomePackCategory | undefined>(currentStaticCategory);

    useEffect(() => {
        const now = getOverrideDate();
        const filteredCurrentCategory = filterCategoryByDate(
            staticsCategories?.categoryProductsSamsung?.find(
                (category) => category.slug === currentStaticCategory?.slug,
            ),
            now,
        );

        setCurrentCategory(filteredCurrentCategory);
    }, [currentStaticCategory?.slug]);

    const currentCategorySlug = useMemo(() => currentCategory?.slug as PACK_HASH, [currentCategory]);
    const faqUrl = useMemo(() => config[currentCategorySlug]?.faqCeUrl, [config, currentCategorySlug]);
    const specificLegals = useMemo(() => config[currentCategorySlug]?.legalNoticesCeUrl, [config, currentCategorySlug]);
    const timelineUrl = useMemo(() => config[currentCategorySlug]?.timelineToShow ?? '', [config, currentCategorySlug]);
    const setSlug = (slug: string) => {
        const now = getOverrideDate();
        const nextCategory = staticsCategories?.categoryProductsSamsung?.find((category) => category.slug === slug);

        if (!nextCategory) {
            return;
        }

        setCurrentCategory(filterCategoryByDate(nextCategory, now));
    };

    const setCmsData = (url: string, setter: (val: string) => void) => {
        cmsService
            .getPartialsCms(url)
            .then((data) => {
                if (data) {
                    setter(data);
                }
            })
            .catch(noop);
    };

    useEffect(() => {
        if (currentCategory?.productsSamsung) {
            setCmsData(`combo/${currentCategory.productsSamsung[0]?.partialsUrlKey}`, setProductDetailsCms);
        }
        if (timelineUrl) {
            setCmsData(timelineUrl, setTimelineDetailsCms);
        }
    }, [currentCategory, timelineUrl]);

    useEffect(() => {
        if (selectedStaticProduct?.mainProduct?.partialsUrlKey) {
            setCmsData(`combo/${selectedStaticProduct?.mainProduct?.partialsUrlKey}`, setProductDetailsCms);
        }
    }, [selectedStaticProduct]);

    return (
        <>
            <PackBody
                defaultPlans={defaultPlans}
                config={config}
                staticsCategories={staticsCategories}
                currentStaticCategory={currentCategory}
                setSelectedStaticProduct={setSelectedStaticProduct}
                selectedStaticProduct={selectedStaticProduct}
                trackingData={trackingData}
                offersByCategory={offersByCategory}
                setSlug={setSlug}
                timelineDetailsCms={timelineDetailsCms}
            />
            <SSIBlock url={partials.MARKETING.FAI_CONTENT} />
            <Cms content={productDetailsCms} />
            <SSIBlock url={partials.MARKETING.FAI_COMMITMENTS} />
            {faqUrl ? (
                <Section>
                    <Container>
                        <Faq
                            url={faqUrl}
                            title={
                                <>
                                    Des questi
                                    <LetterVariant letter='o' />
                                    ns sur nos box + high-tech ?<br /> C&apos;est par ici
                                </>
                            }
                            titleAlt="Des questions sur nos box + high-tech ? C'est par ici"
                        />
                    </Container>
                </Section>
            ) : null}
            <Section>
                <Container>
                    <Breadcrumb slug={currentCategorySlug} config={config} />
                    <Legals id={`pack-${currentCategorySlug}-legals`} url={specificLegals ?? PAGE_SLUG.PACK} />
                </Container>
            </Section>
            <TriggerLogin />
        </>
    );
}
